// PE-6106 — build AppointmentViewModel for bookappointment (IDs assigned by prepareAppointmentViewModelIds on frontend)
import { AppointmentDetailViewModel, AppointmentViewModel } from '../models/appointment-view-model';
import { BookAppointmentSlot } from '../../utils/book-appointment-settings.util';

export interface AppointmentPatientInfo {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  gender?: string;
  preferredName?: string;
}

export interface BuildAppointmentViewModelInput {
  tenantId: string;
  patientId: string;
  appointmentTypeId: string;
  appointmentTypeDescription: string;
  providerId: string;
  providerName: string;
  locationId: string;
  locationName: string;
  slot: BookAppointmentSlot;
  notes?: string;
  colorcode?: string;
  patient?: AppointmentPatientInfo;
  selectedCalendarDate?: Date | null;
}

const APP_NAME = 'patientportal';
const DEFAULT_CODE = '0';

export function buildAppointmentViewModel(input: BuildAppointmentViewModelInput): AppointmentViewModel {
  const tenantId = input.tenantId.trim();
  const rawPatientId = trimPatientUid(input.patientId);
  const patientPostgresId = toPostgresEntityId(rawPatientId, tenantId);
  const providerPostgresId = toPostgresEntityId(input.providerId, tenantId);
  const locationPostgresId = toPostgresEntityId(input.locationId, tenantId);
  const appointmentTypePostgresId = toPostgresEntityId(input.appointmentTypeId, tenantId);

  const selectedDate = input.slot.formattedDate ?? input.slot.slotDate ?? '';
  const { startDateTime, endDateTime } = resolveSlotDateTimes(
    input.slot,
    input.selectedCalendarDate,
    selectedDate
  );

  const durationMinutes = resolveDurationMinutes(startDateTime, endDateTime);
  const today = formatSqlDate(new Date());

  const detail: AppointmentDetailViewModel = {
    tenantid: tenantId,
    issinglerecurrence: 1,
    linenumber: 1,
    appointmentflag: 0,
    asbaseid: providerPostgresId,
    asbasecode: '',
    asbasedesc: input.providerName?.trim() || undefined,
    asbaseflag: 1,
    // PE-6106 — get_patient_appointments requires referenceid '0' (not patient entity id)
    referenceid: DEFAULT_CODE,
    referenceflag: 0,
    startdate: startDateTime ? convertToUtcIso(startDateTime) : '',
    enddate: endDateTime ? convertToUtcIso(endDateTime) : '',
    startdatesql: startDateTime ? formatLocalDateTime(startDateTime) : '',
    enddatesql: endDateTime ? formatLocalDateTime(endDateTime) : '',
    locationid: locationPostgresId,
    locationname: input.locationName?.trim() || undefined,
    departmentid: '',
    departmentname: '',
    colorcode: input.colorcode?.trim() || '',
    notes: sanitizeNotes(input.notes),
    usedstatus: 1,
    templateallocationmasterid: input.slot.nTemplateAllocationMasterID,
    templateallocationid: input.slot.nTemplateAllocationID,
    isparequired: false,
    bremovefromreport: false,
    bPatientBooked: true,
    patientfirstname: input.patient?.firstName?.trim() || undefined,
    patientmiddlename: input.patient?.middleName?.trim() || undefined,
    patientlastname: input.patient?.lastName?.trim() || undefined,
    patientdtdob: formatPatientDob(input.patient?.dateOfBirth),
    gender: mapGenderLabel(input.patient?.gender),
    preferredname: input.patient?.preferredName?.trim() || undefined,
  };

  return {
    tenantid: tenantId,
    issinglerecurrence: 1,
    appointmenttypeid: appointmentTypePostgresId,
    appointmenttypecode: DEFAULT_CODE,
    appointmenttypedesc: input.appointmentTypeDescription?.trim() || undefined,
    asbaseid: providerPostgresId,
    asbasecode: DEFAULT_CODE,
    asbasedesc: input.providerName?.trim() || undefined,
    asbaseflag: 1,
    patientid: patientPostgresId,
    duration: durationMinutes,
    published: today,
    updated: today,
    isseries: false,
    referralproviderid: '',
    referralprovidercode: DEFAULT_CODE,
    referralprovidername: '',
    listAppointmentDetails: [detail],
    appointmentRecurrence: null,
    appname: APP_NAME,
    caremanager: '',
    isallday: false,
    clinicid: 1,
  };
}

// PE-6106 — QPrime appointment.service.ts prepareAppointmentData (IDs before saveAppointment_QP)
export function prepareAppointmentViewModelIds(model: AppointmentViewModel): AppointmentViewModel {
  const tenantId = model.tenantid?.trim();
  if (!tenantId) {
    return model;
  }

  const prepared: AppointmentViewModel = {
    ...model,
    listAppointmentDetails: (model.listAppointmentDetails ?? []).map((d) => ({ ...d })),
  };

  if (!prepared.appointmentid?.trim()) {
    prepared.appointmentid = generateIdWithTenant(tenantId);
  }

  const appointmentId = prepared.appointmentid!;
  const appointmentNumericPart = appointmentId.split('-')[0];
  const emrId = Number(appointmentNumericPart);

  prepared.externalref = { emrid: emrId };

  const generatedPatientTrackingIds: string[] = [];

  prepared.listAppointmentDetails.forEach((detail, index) => {
    if (!detail.tenantid?.trim()) {
      detail.tenantid = tenantId;
    }

    detail.appointmentid = appointmentId;

    if (shouldRegenerateDetailId(detail.appointmentdetailsid, appointmentId)) {
      detail.appointmentdetailsid = generateIdWithTenant(tenantId);
    }

    detail.externalref = { emrid: emrId };

    if (!generatedPatientTrackingIds[index]) {
      generatedPatientTrackingIds[index] = shouldRegenerateDetailId(detail.patienttrackingid, appointmentId)
        ? generateIdWithTenant(tenantId)
        : detail.patienttrackingid!.trim();
    }

    detail.patienttrackingid = generatedPatientTrackingIds[index];
  });

  if (generatedPatientTrackingIds.length > 0) {
    prepared.patienttrackingid = generatedPatientTrackingIds[0];
  }

  return prepared;
}

function shouldRegenerateDetailId(existingId: string | undefined, appointmentId: string): boolean {
  const trimmed = existingId?.trim();
  if (!trimmed) {
    return true;
  }
  return trimmed === appointmentId.trim();
}

// PE-6106 — QPrime appointment.service.ts generateIdWithTenant
export function generateIdWithTenant(tenantId: string): string {
  const timestamp = Date.now().toString();
  const random = Math.floor(Math.random() * 1_000_000)
    .toString()
    .padStart(6, '0');
  const numeric18 = (timestamp + random).slice(0, 18);
  const parts = tenantId.trim().split('-');
  if (parts.length < 2) {
    throw new Error('tenantid must be a UUID with at least two segments');
  }
  const tenantSuffix = `${parts[parts.length - 2]}-${parts[parts.length - 1]}`;
  return `${numeric18}-${tenantSuffix}`;
}

function trimPatientUid(patientId: string): string {
  const trimmed = patientId?.trim() ?? '';
  const dash = trimmed.indexOf('-');
  return dash > 0 ? trimmed.substring(0, dash) : trimmed;
}

function toPostgresEntityId(rawId: string, tenantId: string): string {
  const id = rawId?.trim() ?? '';
  if (!id) {
    return id;
  }
  if (id.includes('-') && id.length > 20) {
    return id;
  }
  const parts = tenantId.split('-');
  if (parts.length < 2) {
    throw new Error('tenantid must be a UUID with at least two segments');
  }
  return `${id}-${parts[parts.length - 2]}-${parts[parts.length - 1]}`;
}

function resolveSlotDateTimes(
  slot: BookAppointmentSlot,
  calendarDate: Date | null | undefined,
  selectedDate: string
): { startDateTime: Date | null; endDateTime: Date | null } {
  const dateParts = resolveSlotDateParts(slot, calendarDate, selectedDate);
  if (!dateParts) {
    return { startDateTime: null, endDateTime: null };
  }

  const startTime = resolveSlotTimeParts(slot, 'start');
  if (!startTime) {
    return { startDateTime: null, endDateTime: null };
  }

  const startDateTime = composeLocalDateTime(dateParts, startTime);
  const endTime = resolveSlotTimeParts(slot, 'end');
  const endDateTime = endTime
    ? composeLocalDateTime(dateParts, endTime)
    : new Date(startDateTime.getTime() + 15 * 60 * 1000);

  return { startDateTime, endDateTime };
}

interface DateParts {
  year: number;
  month: number;
  day: number;
}

interface TimeParts {
  hours: number;
  minutes: number;
  seconds: number;
}

function resolveSlotDateParts(
  slot: BookAppointmentSlot,
  calendarDate: Date | null | undefined,
  selectedDate: string
): DateParts | null {
  if (calendarDate) {
    return {
      year: calendarDate.getFullYear(),
      month: calendarDate.getMonth(),
      day: calendarDate.getDate(),
    };
  }

  const candidates = [slot.formattedDate, slot.slotDate, selectedDate].filter(
    (value): value is string => !!value?.trim()
  );
  for (const candidate of candidates) {
    const parsed = parseSlotDateOnly(candidate);
    if (parsed) {
      return parsed;
    }
  }
  return null;
}

function resolveSlotTimeParts(slot: BookAppointmentSlot, which: 'start' | 'end'): TimeParts | null {
  if (which === 'start') {
    return (
      parseClockTime(slot.formattedTime) ??
      parseClockTime(slot.displayLabel) ??
      parseNumericSlotTime(slot.dtStartTime)
    );
  }

  return parseNumericSlotTime(slot.dtEndTime);
}

function composeLocalDateTime(dateParts: DateParts, timeParts: TimeParts): Date {
  return new Date(
    dateParts.year,
    dateParts.month,
    dateParts.day,
    timeParts.hours,
    timeParts.minutes,
    timeParts.seconds,
    0
  );
}

const MONTH_ABBREVIATIONS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function parseSlotDateOnly(raw: string): DateParts | null {
  const value = raw.trim();
  const usMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    return {
      year: Number(usMatch[3]),
      month: Number(usMatch[1]) - 1,
      day: Number(usMatch[2]),
    };
  }

  const isoMatch = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    return {
      year: Number(isoMatch[1]),
      month: Number(isoMatch[2]) - 1,
      day: Number(isoMatch[3]),
    };
  }

  const dmyMatch = value.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  if (dmyMatch) {
    const monthIndex = MONTH_ABBREVIATIONS.indexOf(dmyMatch[2].toLowerCase());
    if (monthIndex >= 0) {
      return {
        year: Number(dmyMatch[3]),
        month: monthIndex,
        day: Number(dmyMatch[1]),
      };
    }
  }

  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) {
    const date = new Date(parsed);
    return { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
  }

  return null;
}

function parseClockTime(value?: string): TimeParts | null {
  if (!value?.trim()) {
    return null;
  }

  const trimmed = value.trim();
  const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (ampmMatch) {
    let hours = Number(ampmMatch[1]);
    const minutes = Number(ampmMatch[2]);
    const period = ampmMatch[3].toUpperCase();
    if (period === 'PM' && hours < 12) {
      hours += 12;
    }
    if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    return { hours, minutes, seconds: 0 };
  }

  const compactAmPmMatch = trimmed.match(/^(\d{1,2}):(\d{2})([AP]M)$/i);
  if (compactAmPmMatch) {
    let hours = Number(compactAmPmMatch[1]);
    const minutes = Number(compactAmPmMatch[2]);
    const period = compactAmPmMatch[3].toUpperCase();
    if (period === 'PM' && hours < 12) {
      hours += 12;
    }
    if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    return { hours, minutes, seconds: 0 };
  }

  const hmsMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (hmsMatch) {
    return {
      hours: Number(hmsMatch[1]),
      minutes: Number(hmsMatch[2]),
      seconds: Number(hmsMatch[3] ?? 0),
    };
  }

  return null;
}

// PE-6106 — slot dtStartTime/dtEndTime are numeric (minutes-from-midnight or HHmm), not display labels
function parseNumericSlotTime(value?: string): TimeParts | null {
  if (!value?.trim()) {
    return null;
  }

  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return parseClockTime(trimmed);
  }

  if (trimmed.length === 3 || trimmed.length === 4) {
    const hours =
      trimmed.length === 4 ? Number(trimmed.slice(0, 2)) : Number(trimmed.slice(0, 1));
    const minutes =
      trimmed.length === 4 ? Number(trimmed.slice(2, 4)) : Number(trimmed.slice(1, 3));
    if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
      return { hours, minutes, seconds: 0 };
    }
  }

  const totalMinutes = Number(trimmed);
  if (totalMinutes >= 0 && totalMinutes < 24 * 60) {
    return {
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60,
      seconds: 0,
    };
  }

  return null;
}

// PE-6106 — QPrime new-appointment convertToUTC
function convertToUtcIso(date: Date): string {
  return date.toISOString().slice(0, 19);
}

// PE-6106 — QPrime new-appointment formatDateTime (local yyyy-MM-ddTHH:mm:ss)
function formatLocalDateTime(date: Date): string {
  const pad = (value: number): string => value.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatSqlDate(date: Date): string {
  const pad = (value: number): string => value.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function resolveDurationMinutes(start: Date | null, end: Date | null): number {
  if (!start || !end) {
    return 15;
  }
  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) {
    return 15;
  }
  return Math.max(1, Math.floor(diffMs / (60 * 1000)));
}

function sanitizeNotes(notes?: string): string | undefined {
  if (!notes?.trim()) {
    return undefined;
  }
  const sanitized = notes.replace(/\r/g, ' ').replace(/\n/g, ' ').trim();
  return sanitized.length > 2000 ? sanitized.slice(0, 2000) : sanitized;
}

function formatPatientDob(dob?: string): string | undefined {
  if (!dob?.trim()) {
    return undefined;
  }
  const parsed = new Date(dob);
  if (Number.isNaN(parsed.getTime())) {
    return dob.trim();
  }
  return `${formatSqlDate(parsed)}T00:00:00`;
}

function mapGenderLabel(gender?: string): string | undefined {
  if (!gender?.trim()) {
    return undefined;
  }
  const value = gender.trim();
  if (value === '0') {
    return 'Male';
  }
  if (value === '1') {
    return 'Female';
  }
  if (value === '2') {
    return 'Other';
  }
  return value;
}
